import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { TermoCienciaCompromissoMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/termo-ciencia-compromisso-medico/termo-ciencia-compromisso-medico.service';
import { ReportTermoCienciaCompromissoMedicoRequest, ReportTermoCienciaCompromissoMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/termo-ciencia-compromisso-medico/termo-ciencia-compromisso-medico.model';
import { TermoCienciaCompromissoMedicoJobService } from '@/core/services/termo-ciencia-compromisso-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportTermoCienciaCompromissoMedicoRequest;
type Resp = ReportTermoCienciaCompromissoMedicoResponse;

@Component({
    selector: 'app-termo-ciencia-compromisso-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './termo-ciencia-compromisso-medico.html',
    styleUrl: './termo-ciencia-compromisso-medico.scss'
})
/**
 * Relatório PDF assíncrono do Termo de Ciência e Compromisso do médico (o próprio médico assina).
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só resolvemos a identificação do médico e
 * delegamos o HTTP ao service.
 */
export class ReportTermoCienciaCompromissoMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly termoService = inject(TermoCienciaCompromissoMedicoService);
    readonly termoJobService = inject(TermoCienciaCompromissoMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.termoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.termoJobService;
    }

    /** Monta o request a partir da pessoa selecionada; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar o termo.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.termoService.exportarPdf(request);
    }

    /** Limpa o job/preview corrente (o termo não tem formulário de dados a resetar). */
    limparFormulario(): void {
        this.limparJob();
    }
}
